import type { Locale } from '@mengyin/core';

const m = (en: string, zh: string) => ({ en, zh });

/**
 * English is the primary language; Traditional Chinese is a first-class
 * alternative. Keys are flat so lookups stay cheap and typo-checked.
 */
export const messages = {
  'app.name': m('MengYin Study', 'MengYin Study'),
  'app.tagline': m('Revision & practice', '溫習與練習'),

  'nav.today': m('Today', '今日'),
  'nav.decks': m('Decks', '題庫'),
  'nav.plaza': m('Plaza', '廣場'),
  'nav.generate': m('Generate', 'AI 出題'),
  'nav.review': m('Review', '錯題'),
  'nav.stats': m('Statistics', '統計'),
  'nav.plugins': m('Plugins', '插件'),
  'nav.settings': m('Settings', '設定'),

  'side.streak': m('{n}-day streak', '連續 {n} 日'),
  'side.due': m('{n} due', '{n} 題到期'),

  'common.questions': m('questions', '題'),
  'common.decks': m('decks', '題庫'),
  'common.due': m('due', '待複習'),
  'common.mistakes': m('mistakes', '錯題'),
  'common.study': m('Study', '練習'),
  'common.test': m('Test', '測驗'),
  'common.preview': m('Preview', '預覽'),
  'common.cancel': m('Cancel', '取消'),
  'common.remove': m('Remove', '移除'),
  'common.never': m('Not started', '未開始'),
  'common.loading': m('Loading decks and plugins…', '載入題庫與插件…'),
  'common.all': m('All', '全部'),
  'common.none': m('None', '無'),
  'common.retry': m('Retry', '重試'),

  'dash.title': m('Today', '今日'),
  'dash.lede': m('{due} due · {weak} weak · {streak}-day streak', '{due} 題待複習 · {weak} 題薄弱 · 連續 {streak} 日'),
  'dash.startReview': m('Review {count} due', '複習 {count} 題'),
  'dash.nothingDue': m('Nothing due today', '今日沒有到期題目'),
  'dash.random': m('Random 10', '隨機 10 題'),
  'dash.browse': m('Browse decks', '瀏覽題庫'),
  'dash.metric.due': m('Due today', '待複習'),
  'dash.metric.mistakes': m('Mistakes', '錯題'),
  'dash.metric.mastered': m('Mastered', '已掌握'),
  'dash.metric.accuracy': m('Accuracy', '正確率'),
  'dash.metric.dueHint': m('scheduled by the review queue', '按複習排程到期'),
  'dash.metric.mistakesHint': m('wrong on the last attempt', '最近一次答錯'),
  'dash.metric.masteredHint': m('two correct in a row', '連續答對兩次'),
  'dash.metric.accuracyHint': m('{n} answers so far', '累計 {n} 次作答'),
  'dash.continue': m('Continue', '繼續學習'),
  'dash.recent': m('Recent sessions', '最近練習'),
  'dash.allDecks': m('All decks', '全部題庫'),
  'dash.continueMeta': m('{seen} of {total} studied · {accuracy} accuracy', '已練 {seen} / {total} · 正確率 {accuracy}'),
  'dash.noContinue': m('Nothing studied yet', '還沒有學習記錄'),
  'dash.noContinueHint': m('Pick a deck and answer a few questions.', '揀一組題目，先做幾題。'),
  'dash.noSessions': m('No sessions yet', '還沒有完成練習'),
  'dash.noSessionsHint': m('Finish a session and the result shows up here.', '完成一次練習後，結果會顯示在這裡。'),
  'dash.emptyTitle': m('No question packs yet', '尚未載入題庫'),
  'dash.emptyBody': m('No decks are in your library yet. Add one from the Plaza or import a folder.', '你的題庫還是空的。可到廣場加入官方題庫，或從本機匯入題庫。'),
  'dash.openPlugins': m('Manage plugins', '管理插件'),
  'dash.openPlaza': m('Open the Plaza', '前往廣場'),
  'dash.todayLog': m('{n} answers today', '今日 {n} 次作答'),

  'lib.title': m('Decks', '題庫'),
  'lib.subtitle': m('{decks} decks · {questions} questions', '{decks} 組題庫 · {questions} 題'),
  'lib.search': m('Search decks, tags, plugins', '搜尋題庫、標籤、插件'),
  'lib.filter.all': m('All', '全部'),
  'lib.filter.due': m('Due', '待複習'),
  'lib.filter.mistakes': m('Mistakes', '有錯題'),
  'lib.filter.new': m('New', '未開始'),
  'lib.col.deck': m('Deck', '題庫'),
  'lib.col.studied': m('Studied', '進度'),
  'lib.col.due': m('Due', '到期'),
  'lib.col.accuracy': m('Accuracy', '正確率'),
  'lib.empty': m('No decks match', '沒有符合條件的題庫'),
  'lib.emptyHint': m('Try another filter, or add an official deck from the Plaza.', '試其他篩選條件，或到廣場加入官方題庫。'),
  'lib.rescan': m('Rescan plugins', '重新掃描插件'),
  'lib.openPlaza': m('Browse the Plaza', '前往廣場'),
  'lib.guestBadge': m('Starter', '入門'),
  'lib.remove': m('Remove deck', '移除題庫'),
  'lib.removeTitle': m('Remove this deck?', '移除這個題庫？'),
  'lib.removeBody': m('“{name}” will be removed from your library. Questions and progress are kept, so adding it again restores the deck.', '「{name}」會從你的題庫移除。題目與學習進度會保留，日後重新加入即可恢復。'),
  'lib.removeHint': m('This does not delete the official catalogue or another learner’s copy.', '這不會刪除官方目錄，也不會影響其他學習者的副本。'),
  'lib.removeConfirm': m('Remove', '移除'),
  'lib.cannotRemove': m('The starter deck is always available and cannot be removed.', '入門題庫永遠可用，不能移除。'),
  'lib.reviewDue': m('Review {n} due', '複習 {n} 題'),
  'lib.test20': m('Test 20 questions', '測驗 20 題'),
  'lib.startStudy': m('Start studying', '開始練習'),
  'lib.previewMeta': m('{n} questions · {seen} studied · {accuracy} accuracy', '{n} 題 · 已練 {seen} 題 · 正確率 {accuracy}'),
  'lib.rowMeta': m('{plugin} · {n} questions', '{plugin} · {n} 題'),

  'mode.practice': m('Practice', '普通模式'),
  'mode.drill': m('Muscle memory', '肌肉記憶'),
  'mode.exam': m('Exam', '考試模式'),
  'mode.review': m('Mistakes', '錯題重溫'),
  'mode.srs': m('Spaced review', '間隔複習'),
  'mode.random': m('Random', '隨機抽題'),
  'mode.practiceHint': m('Immediate feedback with explanations. Work at your own pace.', '即時批改並顯示解析，自己控制節奏。'),
  'mode.drillHint': m('Fast rounds. Wrong answers come back until you get them right.', '快速作答；答錯的題目會排回隊尾，直到答對。'),
  'mode.examHint': m("Timed to this deck's length. No feedback until the end.", '按題庫時限作答，結束後才顯示結果。'),
  'mode.srsHint': m('Only questions the schedule says are due.', '只練排程上今天到期的題目。'),
  'mode.reviewHint': m('Only questions you got wrong last time.', '只練上次答錯的題目。'),
  'mode.randomHint': m('A mixed drill across every deck.', '從所有題庫中隨機抽題。'),

  'modes.title': m('Choose a mode', '選擇模式'),
  'modes.count': m('{n} questions', '{n} 題'),
  'modes.none': m('Nothing to practise here yet', '暫時沒有可練習的題目'),
  'modes.start': m('Start', '開始'),
  'modes.min': m('min', '分鐘'),

  'strength.new': m('Not started', '未作答'),
  'strength.weak': m('Weak', '薄弱'),
  'strength.learning': m('Learning', '學習中'),
  'strength.solid': m('Solid', '穩定'),
  'strength.mastered': m('Mastered', '精通'),

  'type.single': m('Multiple choice', '單選題'),
  'type.multi': m('Select all that apply', '多選題'),
  'type.boolean': m('True or false', '是非題'),
  'type.fill': m('Fill in the blank', '填空題'),
  'type.short': m('Short answer', '簡答題'),
  'type.code': m('Code', '程式題'),

  'practice.questionOf': m('Question {index} of {total}', '第 {index} / {total} 題'),
  'practice.showHint': m('Show hint', '顯示提示'),
  'practice.hideHint': m('Hide hint', '收起提示'),
  'practice.yourAnswer': m('Your answer', '你的答案'),
  'practice.typeAnswer': m('Type your answer, then press Enter.', '輸入答案後按 Enter 提交。'),
  'practice.writeAnswer': m('Write your answer, then compare it with the model answer.', '寫下你的答案，再對照參考答案自評。'),
  'practice.modelAnswer': m('Model answer', '參考答案'),
  'practice.selfGrade': m('How did you do?', '對照後自評'),
  'practice.gradeCorrect': m('Correct', '完全正確'),
  'practice.gradePartial': m('Partly', '部分正確'),
  'practice.gradeWrong': m('Incorrect', '不正確'),
  'practice.showModel': m('Show model answer', '顯示參考答案'),
  'practice.submit': m('Submit', '提交'),
  'practice.next': m('Next', '下一題'),
  'practice.prev': m('Previous', '上一題'),
  'practice.skip': m('Skip', '跳過'),
  'practice.results': m('See results', '查看結果'),
  'practice.end': m('End session', '結束練習'),
  'practice.submitExam': m('Submit exam', '交卷'),
  'practice.submitExamTitle': m('Submit the exam?', '確定交卷？'),
  'practice.submitExamBody': m('You can still change answers before submitting. Once submitted the exam is marked immediately.', '交卷前仍可修改答案；一旦交卷會即時批改並顯示成績。'),
  'practice.submitExamConfirm': m('Submit exam', '確定交卷'),
  'practice.unanswered': m('{n} questions have no answer yet.', '有 {n} 題尚未作答。'),
  'practice.flag': m('Flag', '標記'),
  'practice.recorded': m('Answer recorded. Results appear at the end.', '已記錄答案，測驗結束後才顯示結果。'),
  'practice.autoNext': m('Next question in a moment…', '稍後自動前往下一題…'),
  'practice.leaveTitle': m('Leave this session?', '離開這次練習？'),
  'practice.leaveBody': m('Answers already submitted still count towards your statistics, but this session will have no final result.', '已作答的題目仍會計入統計，但這次練習不會產生完整結果。'),
  'practice.leave': m('Leave', '離開'),
  'practice.keepGoing': m('Keep going', '繼續練習'),
  'practice.shortcuts': m('1–9 choose · Enter submit or next · ← → move · F flag', '1–9 選項 · Enter 提交／下一題 · ← → 切換 · F 標記'),
  'practice.answerCount': m('{n} answered', '已答 {n} 題'),
  'practice.answerPlaceholder': m('Type your answer', '輸入答案'),
  'practice.partialHint': m('Partly correct — select every option that applies.', '部分正確：你選對了一些，但未全部選中。'),
  'practice.multiHint': m('Select all that apply — more than one answer may be correct.', '可多選：可能有多於一個正確答案。'),
  'answer.true': m('True', '正確'),
  'answer.false': m('False', '錯誤'),

  'code.blankLabel': m('One expression or statement', '一段或一句程式碼'),
  'code.fillBlank': m('Fill in the blank', '填寫空白'),
  'code.body': m('Function body', '函式內容'),
  'code.bodyPlaceholder': m('Write the body of the function…', '填寫函式內容…'),
  'code.query': m('Whole query', '整段查詢'),
  'code.queryPlaceholder': m('Write the complete SQL query…', '填寫完整的 SQL 查詢…'),
  'code.blankN': m('Blank {n}', '空白 {n}'),
  'code.blankCount': m('{n} blanks', '{n} 個空白'),
  'code.fillAll': m('Fill every blank to run', '填好所有空白才能執行'),
  'code.placeholder': m('e.g. nums.reduce((a, b) => a + b, 0)', '例如 nums.reduce((a, b) => a + b, 0)'),
  'code.placeholderTypescript': m('e.g. nums.reduce((a: number, b: number) => a + b, 0)', '例如 nums.reduce((a: number, b: number) => a + b, 0)'),
  'code.placeholderPython': m('e.g. sum(n for n in nums)', '例如 sum(n for n in nums)'),
  'code.placeholderSql': m('e.g. SELECT name FROM students WHERE score > 80', '例如 SELECT name FROM students WHERE score > 80'),
  'code.placeholderJava': m('e.g. sum += scanner.nextInt();', '例如 sum += scanner.nextInt();'),
  'code.placeholderCpp': m('e.g. sum += x;', '例如 sum += x;'),
  'code.program': m('Program preview', '程式預覽'),
  'code.providedData': m('Provided test data', '已提供的測試資料'),
  'code.stdin': m('Standard input', '標準輸入'),
  'code.stdinHint': m(
    'The test feeds the text above to the program\'s standard input. Read it with cin / Scanner; write the answer to stdout.',
    '測試會把上方文字送入程式的標準輸入；用 cin／Scanner 讀取，並把答案輸出到標準輸出。',
  ),
  'code.contextHint': m(
    'The lines before the highlighted gap are supplied by the question; you only fill the gap.',
    '反白空白之前的程式碼由題目提供，你只需填寫空白位置。',
  ),
  'code.contextHintMultiple': m(
    'Test 1 data is shown above the gap. Each test supplies its own data; you only fill the gap.',
    '這裡顯示第 1 個測試的資料；每個測試會提供自己的資料，你只需填寫空白位置。',
  ),
  'code.missingInput': m(
    'This generated question uses input but does not show a value for it. The question is incomplete; skip it or regenerate the deck.',
    '這道生成題目使用了 input，但沒有顯示它的值。題目資料不完整，請跳過或重新生成題庫。',
  ),
  'code.gap': m('fill this in', '在這裡填寫'),
  'code.runtimeReady': m('{runtime} {version} runs locally in your browser.', '{runtime} {version} 在你的瀏覽器本機執行。'),
  'code.runtimeMissing': m(
    'The {language} runtime is not installed in this build. Run npm run runtimes:fetch, then rebuild.',
    '這個版本沒有包含 {language} runtime。請先執行 npm run runtimes:fetch 再重新建置。',
  ),
  'code.run': m('Run', '執行'),
  'code.running': m('Running…', '執行中…'),
  'code.testsPassed': m('{passed}/{total} tests passed', '{passed}/{total} 項測試通過'),
  'code.output': m('Output', '輸出'),
  'code.pass': m('PASS', '通過'),
  'code.fail': m('FAIL', '未通過'),
  'code.noOutput': m('(no output)', '（沒有輸出）'),
  'code.expected': m('Expected:', '預期輸出：'),
  'code.examLocked': m(
    'Code execution is off in exam mode. Type your answer — it is kept and reviewed with the paper.',
    '考試模式不允許執行程式碼。你仍可填寫答案，交卷後一併檢視。',
  ),
  'code.runFailed': m('Could not run the code: {message}', '無法執行程式碼：{message}'),
  'code.broken': m('This code question is missing its template.', '這道程式題缺少模板設定。'),

  'verdict.correct': m('Correct', '答對'),
  'verdict.partial': m('Partly correct', '部分正確'),
  'verdict.incorrect': m('Incorrect', '答錯'),
  'verdict.pending': m('Not graded', '待批改'),
  'verdict.yourAnswer': m('Your answer', '你的答案'),
  'verdict.correctAnswer': m('Correct answer', '正確答案'),

  'results.title': m('Session results', '練習結果'),
  'results.score': m('Score', '得分'),
  'results.correct': m('Correct', '正確'),
  'results.partial': m('Partly', '部分'),
  'results.incorrect': m('Incorrect', '錯誤'),
  'results.skipped': m('Skipped', '跳過'),
  'results.correctOf': m('{correct} of {total} correct', '答對 {correct} / {total} 題'),
  'results.retryWrong': m('Retry missed ({n})', '重做未答對（{n}）'),
  'results.repeat': m('Repeat session', '再練一次'),
  'results.backToDecks': m('Back to decks', '返回題庫'),
  'results.byTag': m('By tag', '標籤表現'),
  'results.byTagHint': m('Lower means weaker', '比率越低越需要複習'),
  'results.review': m('Question review', '逐題回顧'),
  'results.onlyMistakes': m('Only mistakes', '只看錯題'),
  'results.allCorrect': m('Every question was correct.', '全部答對，沒有需要回顧的題目。'),

  'review.title': m('Review', '錯題與標記'),
  'review.subtitle': m('Work through what you have missed, one question at a time.', '集中處理未掌握的題目，逐題看解析再重做。'),
  'review.tab.mistakes': m('Mistakes {n}', '錯題 {n}'),
  'review.tab.flagged': m('Flagged {n}', '已標記 {n}'),
  'review.reviewAll': m('Review all ({n})', '開始重溫（{n}）'),
  'review.hintMistakes': m('Last attempt was wrong or partial', '最近一次作答為錯誤或部分'),
  'review.hintFlagged': m('Flagged during practice with F', '練習時按 F 標記的題目'),
  'review.emptyMistakes': m('No mistakes', '暫時沒有錯題'),
  'review.emptyMistakesBody': m('Questions you miss appear here automatically.', '答錯的題目會自動出現在這裡。'),
  'review.emptyFlagged': m('Nothing flagged', '還沒有標記任何題目'),
  'review.emptyFlaggedBody': m('Press F while practising to flag a question.', '練習時按 F 標記題目。'),
  'review.goDecks': m('Go to decks', '前往題庫'),
  'review.lastAnswer': m('Last answer', '上次答案'),
  'review.note': m('Note', '筆記'),
  'review.addNote': m('Add note', '添加筆記'),
  'review.saveNote': m('Save note', '儲存筆記'),
  'review.notePlaceholder': m('A note to yourself — stored on this device.', '寫給自己的筆記，只存於本機。'),
  'review.flag': m('Flag', '標記'),
  'review.unflag': m('Unflag', '取消標記'),
  'review.studyOne': m('Study this one', '只練這題'),
  'review.deckLine': m('{deck} · {date} · {n} attempts', '{deck} · {date} · 累計 {n} 次'),
  'review.statusWrong': m('Wrong', '錯誤'),
  'review.statusPartial': m('Partial', '部分'),
  'review.statusFlagged': m('Flagged', '已標記'),

  'stats.title': m('Statistics', '統計'),
  'stats.subtitle': m('{n} answers · {time} studied', '累計 {n} 次作答 · 學習 {time}'),
  'stats.streak': m('{current}-day streak · best {best}', '連續 {current} 日 · 最佳 {best} 日'),
  'stats.overall': m('Overall accuracy', '整體正確率'),
  'stats.fullyCorrect': m('{n} fully correct', '{n} 題完全答對'),
  'stats.studied': m('Questions studied', '已練習題目'),
  'stats.studiedHint': m('in the review queue', '已進入複習排程'),
  'stats.avgTime': m('Average time', '平均用時'),
  'stats.avgTimeHint': m('per question', '每題'),
  'stats.today': m('Today', '今日'),
  'stats.todayHint': m('goal {n}', '目標 {n} 題'),
  'stats.last14': m('Answers, last 14 days', '近 14 日作答'),
  'stats.due7': m('Due in the next 7 days', '未來 7 日到期'),
  'stats.due7Hint': m('spaced repetition schedule', '間隔複習排程'),
  'stats.due7Foot': m('Due questions appear on Today.', '到期題目會顯示在「今日」頁。'),
  'stats.tags': m('Tag mastery', '標籤掌握度'),
  'stats.tagsTrained': m('{n} practised', '{n} 個已練習標籤'),
  'stats.showAll': m('Show all', '顯示全部'),
  'stats.showTrained': m('Show practised', '只顯示已練'),
  'stats.tagsEmpty': m('Practise a few questions and this fills in.', '開始練習後會顯示。'),
  'stats.decks': m('Deck progress', '各題庫進度'),
  'stats.deckCol': m('Deck', '題庫'),
  'stats.progressCol': m('Studied', '進度'),
  'stats.answersCol': m('Answers', '作答'),
  'stats.accuracyCol': m('Accuracy', '正確率'),
  'stats.modes': m('Sessions by mode', '練習模式分佈'),
  'stats.notPractised': m('Not practised', '未練'),
  'stats.dayN': m('+{n}d', '+{n} 日'),
  'stats.emptyTitle': m('No data yet', '還沒有數據'),
  'stats.emptyBody': m('Finish a session and your numbers appear here.', '完成一次練習，統計便會自動更新。'),

  'plaza.title': m('Plaza', '題庫廣場'),
  'plaza.subtitle': m('Official question banks. Add the ones you want to your own library.', '官方題庫一覽；把想練的加入自己的題庫。'),
  'plaza.search': m('Search official decks, tags or packs', '搜尋官方題庫、標籤或合集'),
  'plaza.count': m('{packs} packs · {added} in your library', '{packs} 個合集 · 已加入 {added} 個'),
  'plaza.guestNote': m(
    'You can browse every official deck without signing in. Sign in to add decks, or keep practising the free starter decks.',
    '未登入也可以瀏覽全部官方題庫；登入後才可加入自己的題庫。未登入仍可練習免費入門題庫。',
  ),
  'plaza.memberNote': m(
    'Added decks appear in Decks and sync automatically across your signed-in devices. Official updates follow the catalogue.',
    '加入的題庫會出現在「題庫」頁，並自動同步到已登入的裝置；官方更新會跟隨目錄版本。',
  ),
  'plaza.libraryError': m('Your added decks could not be loaded.', '無法載入你已加入的題庫。'),
  'plaza.empty': m('The Plaza is empty', '廣場暫時沒有題庫'),
  'plaza.emptyHint': m(
    'No official deck is published yet. The starter decks are still available in Decks.',
    '官方目錄尚未發布題庫；你仍可在「題庫」頁練習入門題庫。',
  ),
  'plaza.offline': m(
    'The Plaza needs the backend to list official decks. The bundled guest decks still work.',
    '廣場需要連接後端才能顯示官方題庫；內建入門題庫仍可練習。',
  ),
  'plaza.offlineHint': m(
    'Connect the Supabase backend and run the migrations to browse official decks.',
    '請設定 Supabase 後端並套用 migration，才可瀏覽官方題庫。',
  ),
  'plaza.deckCount': m('{n} decks', '{n} 個題庫'),
  'plaza.questionCount': m('{n} questions', '{n} 題'),
  'plaza.add': m('Add to my decks', '加入我的題庫'),
  'plaza.added': m('Added', '已加入'),
  'plaza.signInToAdd': m('Sign in to add', '登入後加入'),
  'plaza.addedToast': m('Added to your library', '已加入你的題庫'),
  'plaza.removedToast': m('Removed from your library', '已從你的題庫移除'),
  'plaza.guestHint': m(
    'Guest practice is carried into a new account. An existing account keeps its own statistics — signing out starts from zero.',
    '訪客練習會帶入新帳戶；已有帳戶則保留自己的統計，登出後由零開始。',
  ),

  'plugins.title': m('Plugins', '插件'),
  'plugins.subtitle': m(
    'Plugins add functionality — question types, importers and runtime behaviour. Decks are content and live in the Plaza.',
    '插件提供功能——題型、匯入器與執行期行為；題庫是內容，放在廣場。',
  ),
  'plugins.rescan': m('Rescan', '重新掃描'),
  'plugins.importFolder': m('Import folder', '匯入資料夾'),
  'plugins.importFile': m('Import file', '匯入檔案'),
  'plugins.issues': m('Load issues ({n})', '載入問題（{n}）'),
  'plugins.issuesHint': m('Fix the plugin, then rescan.', '修正插件後按「重新掃描」。'),
  'plugins.empty': m('No plugins loaded', '尚未載入插件'),
  'plugins.emptyBody': m('Add a folder under plugins/, run npm run plugins:sync, then rescan.', '把插件資料夾放到 plugins/，執行 npm run plugins:sync，再按重新掃描。'),
  'plugins.builtin': m('Built-in', '內建'),
  'plugins.local': m('Local', '本機'),
  'plugins.cloud': m('Cloud', '雲端'),
  'plugins.cloudOnlyYou': m('Only you can read this', '只有你可讀取'),
  'plugins.cloudHint': m(
    'Private to your account; visible on every signed-in device. Runtime modules execute there, so only upload code you trust.',
    '只限你的帳戶，登入後可在其他裝置查看。Runtime 模組會在其他裝置執行，請只上傳你信任的程式碼。',
  ),
  'plugins.uploadCloud': m('Upload to cloud', '上傳到雲端'),
  'plugins.uploadCloudLogin': m('Sign in to upload', '登入後上傳'),
  'plugins.cloudUploaded': m('Uploaded {n} deck(s) to your cloud.', '已上傳 {n} 個題庫到雲端。'),
  'plugins.removeCloud': m('Remove from cloud', '從雲端移除'),
  'plugins.cloudRemoved': m('Removed from the cloud.', '已從雲端移除。'),
  'plugins.cloudLoading': m('Loading cloud decks…', '正在載入雲端題庫…'),
  'plugins.cloudError': m('Cloud decks could not be loaded.', '雲端題庫載入失敗。'),
  'plugins.cloudSignedOut': m(
    'Generated decks are stored in your account. Sign in to load them from the server.',
    '生成題庫存在你的帳戶中；請登入後從伺服器載入。',
  ),
  'plugins.autoSync': m(
    'Cloud upload is automatic. Decks and AI-generated questions are saved to your account as soon as you are signed in; there is no upload button.',
    '雲端上傳全自動：登入後，題庫與 AI 生成結果會即時儲存到你的帳戶，不需按「上傳」。',
  ),
  'plugins.officialFeatures': m('Official features', '官方功能'),
  'plugins.codeFeature': m('Code questions in six languages', '六種語言的程式題'),
  'plugins.codeFeatureDesc': m(
    'The official code-question plugin. JavaScript, TypeScript, Python, SQL, Java and C++ run locally in an isolated browser sandbox — no execution server — and nothing is run in exams.',
    '官方程式題插件。JavaScript、TypeScript、Python、SQL、Java 與 C++ 都在瀏覽器的隔離沙盒本機執行，不需伺服器；考試模式永不執行。',
  ),
  'plugins.codeLanguages': m(
    'JavaScript · TypeScript · Python · SQL · Java · C++',
    'JavaScript · TypeScript · Python · SQL · Java · C++',
  ),
  'plugins.examSafe': m('Exam-safe: answer stored, never run', '考試安全：只保存答案，不執行'),
  'plugins.features': m('Feature plugins', '功能插件'),
  'plugins.featureCount': m('{n} loaded', '已載入 {n} 個'),
  'plugins.noFeatures': m('No feature plugins loaded', '尚未載入功能插件'),
  'plugins.noFeaturesHint': m(
    'Decks do not need a plugin. Add a plugin only when you need a new question type, importer or runtime hook.',
    '題庫不需要插件；只有需要新題型、匯入器或執行期 hook 時才要加插件。',
  ),
  'plugins.typeCount': m('{n} question types', '{n} 種題型'),
  'plugins.featureNoTypes': m('No custom question types', '沒有自訂題型'),
  'plugins.removeFeature': m('Remove feature plugin', '移除功能插件'),
  'plugins.yourDecks': m('Your decks', '你的題庫'),
  'plugins.noDecks': m('No decks yet', '還沒有題庫'),
  'plugins.noDecksHint': m('Add an official deck in the Plaza, or import one from this page.', '可到廣場加入官方題庫，或在此頁匯入題庫。'),
  'plugins.deckDeleteHint': m(
    'Removing a deck also removes its cloud copy when one exists. Learning progress is kept.',
    '移除題庫時會一併刪除雲端副本（如有）；學習進度會保留。',
  ),
  'plugins.deckCount': m('{n} decks', '{n} 題庫'),
  'plugins.deckCountOne': m('1 deck', '1 題庫'),
  'plugins.questionCount': m('{n} questions', '{n} 題'),
  'plugins.questionCountOne': m('1 question', '1 題'),
  'plugins.by': m('by {author}', '作者 {author}'),
  'plugins.runtime': m('runtime module', '含執行期程式碼'),
  'plugins.customTypes': m('Registered question types', '已註冊題型'),
  'plugins.typesHint': m('{n} types', '{n} 種'),
  'plugins.customTypeHint': m('Plugins can register new question types and importers; decks stay separate content.', '插件可註冊新題型與匯入器；題庫是獨立內容。'),
  'plugins.add': m('Add a feature plugin', '新增功能插件'),
  'plugins.step1': m('Create plugins/<id>/', '建立資料夾 plugins/<id>/'),
  'plugins.step2': m('Add manifest.json and, if needed, an entry module', '加入 manifest.json，需要時再加入 entry 模組'),
  'plugins.step3': m('Run npm run plugins:sync', '執行 npm run plugins:sync'),
  'plugins.step4': m('Reload and check Plugins', '重新整理後到「插件」頁'),
  'plugins.guide': m('Full format and extension points: docs/PLUGIN_GUIDE.md', '完整格式與擴充方法見 docs/PLUGIN_GUIDE.md'),
  'plugins.importedNote': m(
    'Imported decks are stored in IndexedDB and upload automatically after sign-in. A folder may also carry an entry module.',
    '本機匯入的題庫存於 IndexedDB，登入後會自動上傳；資料夾也可附帶 entry 模組。',
  ),

  'settings.title': m('Settings', '設定'),
  'settings.subtitle': m('Appearance, practice behaviour and data.', '外觀、練習行為與資料。'),

  'gen.title': m('Generate questions', 'AI 生成題目'),
  'gen.subtitle': m(
    'Turn your own notes, slides or PDFs into a practice deck with your own AI API.',
    '用你自己的 AI API，把筆記、投影片或 PDF 變成練習題庫。',
  ),
  'gen.topic': m('Topic', '課題'),
  'gen.topicPlaceholder': m('e.g. Data structures — week 3', '例如：資料結構 — 第三週'),
  'gen.description': m('Description (optional with materials)', '描述（有材料時可選）'),
  'gen.descriptionPlaceholder': m(
    'Describe the syllabus, the exact topics, or the questions you want. This is enough without a file.',
    '描述課程範圍、具體主題或想要的題目；沒有材料時只寫這裡也可以。',
  ),
  'gen.descriptionHint': m(
    'Used as the source brief. Add files as well if you have them.',
    '會作為出題依據；有材料時也可以同時填寫。',
  ),
  'gen.request': m('Request', '題目設定'),
  'gen.count': m('Questions', '題目數量'),
  'gen.difficulty': m('Difficulty', '難度'),
  'gen.difficultyMixed': m('Mixed', '混合'),
  'gen.difficultyEasy': m('Easier', '較易'),
  'gen.difficultyHard': m('Harder', '較難'),
  'gen.language': m('Question language', '題目語言'),
  'gen.langAuto': m('Match material', '跟隨材料'),
  'gen.langEn': m('English', '英文'),
  'gen.langZh': m('繁體中文', '繁體中文'),
  'gen.types': m('Question types', '題目類型'),
  'gen.typesHint': m(
    'Pick one or more. A single type produces only that type.',
    '可選一個或多個；只選一種時，全部題目都會是該類型。',
  ),
  'gen.typesCodeHint': m(
    'Code questions run only in practice; exams accept the answer but never execute it.',
    '程式題只在練習時執行；考試只接受答案，不會執行。',
  ),
  'gen.codeLanguage': m('Code language', '程式語言'),
  'gen.codeLanguageHint': m(
    'Used only when code questions are generated.',
    '只在生成程式題時使用。',
  ),
  'gen.typesRequired': m('Keep at least one question type selected.', '請至少保留一種題型。'),
  'gen.focus': m('Focus (optional)', '重點（可選）'),
  'gen.focusPlaceholder': m('e.g. only chapter 4; emphasise recursion', '例如：只考第四章；重點放在遞迴'),
  'gen.materials': m('Materials', '材料'),
  'gen.materialsHint': m(
    'PDF, DOCX, PPTX, TXT, MD, CSV or JSON — up to {limit} in total. Only text is read; images are ignored.',
    'PDF、DOCX、PPTX、TXT、MD、CSV 或 JSON，合共最多 {limit}。只讀取文字，不處理圖片。',
  ),
  'gen.materialsPrivacy': m(
    'Materials are deleted automatically as soon as generation finishes.',
    '生成完成後，材料會自動刪除。',
  ),
  'gen.addFiles': m('Add files', '加入檔案'),
  'gen.drop': m('Drop files here, or choose them', '把檔案拖到這裡，或選擇檔案'),
  'gen.budget': m('{used} of {limit} · {chars} characters', '已用 {used} / {limit} · {chars} 字元'),
  'gen.emptyMaterials': m('No material yet', '尚未加入材料'),
  'gen.descriptionOnly': m(
    'No file needed — the description above will be used.',
    '不需檔案——會使用上面的描述出題。',
  ),
  'gen.chars': m('chars', '字元'),
  'gen.remove': m('Remove', '移除'),
  'gen.truncated': m('truncated', '已截斷'),
  'gen.generate': m('Generate questions', '生成題目'),
  'gen.generating': m('Generating…', '生成中…'),
  'gen.cancel': m('Cancel', '取消'),
  'gen.cancelled': m('Generation cancelled', '已取消生成'),
  'gen.needTopic': m(
    'Give the set a topic, or start the description with one.',
    '請填寫課題，或在描述第一行寫明主題。',
  ),
  'gen.needMaterials': m('Add at least one material file.', '請至少加入一份材料。'),
  'gen.needSource': m(
    'Add a material file or write a description first.',
    '請先加入材料，或填寫描述。',
  ),
  'gen.needConfig': m('Configure your AI provider in Settings first.', '請先在設定頁配置 AI 供應商。'),
  'gen.signInTitle': m('Sign in to generate questions', '登入後才可使用 AI 出題'),
  'gen.signInBody': m(
    'AI generation and your own API key are available to signed-in learners. Guests can still study the starter decks.',
    'AI 出題與自訂 API 金鑰只開放給已登入的學習者；訪客仍可練習入門題庫。',
  ),
  'gen.guestKeepsProgress': m(
    'Guest progress is kept when you sign in.',
    '登入後會保留訪客期間的學習進度。',
  ),
  'gen.preview': m('Preview', '預覽'),
  'gen.examTime': m('Selected exam length: {n} min', '已選題目考試時間：{n} 分鐘'),
  'gen.previewHint': m(
    '{n} questions ready. Untick anything you do not want.',
    '已生成 {n} 題，可取消勾選不要的題目。',
  ),
  'gen.save': m('Save as deck', '儲存為題庫'),
  'gen.saveNeedsLogin': m(
    'Sign in to save a generated deck; generated decks are private to your account.',
    '請先登入才能儲存生成的題庫；生成題庫只屬於你的帳戶。',
  ),
  'gen.discard': m('Discard', '放棄'),
  'gen.warnings': m('Warnings', '提示'),
  'gen.usage': m('{tokens} tokens · {model}', '{tokens} tokens · {model}'),
  'gen.viewSkill': m('View the authoring skill', '查看出題 Skill'),
  'gen.skillHint': m(
    'This is the exact instruction set sent to your model. Version {version}.',
    '以下就是發送給模型的完整指令，版本 {version}。',
  ),
  'gen.copy': m('Copy', '複製'),
  'gen.copied': m('Copied', '已複製'),
  'gen.copyFailed': m('Could not copy — select the text instead.', '無法複製，請直接選取文字。'),
  'gen.noConfigTitle': m('AI provider not configured', '尚未配置 AI 供應商'),
  'gen.noConfigBody': m(
    'Add an OpenAI-compatible base URL, key and model in Settings.',
    '請在設定頁填寫 OpenAI 相容的 API 網址、金鑰與模型。',
  ),
  'gen.openSettings': m('Open settings', '前往設定'),
  'gen.started': m('Generating batch {batch} of {batches}…', '正在生成第 {batch} / {batches} 批…'),
  'gen.validating': m('Validating questions…', '正在驗證題目…'),
  'gen.repairing': m('The model reply was malformed; asking again…', '模型回覆格式有誤，正在重試…'),
  'gen.splitting': m(
    'The reply hit the output limit; splitting into smaller batches…',
    '回覆達到長度上限，正在拆成更小的批次…',
  ),
  'gen.results': m('Generated questions', '生成結果'),
  'gen.selectAll': m('Select all', '全選'),
  'gen.selected': m('{n} selected', '已選 {n} 題'),
  'gen.answer': m('Answer', '答案'),
  'gen.template': m('Template', '模板'),
  'gen.tests': m('Tests', '測試'),
  'gen.details': m('Show details', '顯示詳情'),
  'gen.ingesting': m('Reading {name}…', '正在讀取 {name}…'),
  'gen.noText': m('No usable text was found in these files.', '這些檔案沒有可用的文字。'),
  'gen.savedNote': m('Saved to Decks — open it to practise.', '已存到題庫，可立即練習。'),
  'gen.removeMaterial': m('Remove material', '移除材料'),

  'settings.appearance': m('Appearance', '外觀'),
  'settings.language': m('Language', '語言'),
  'settings.theme': m('Theme', '主題'),
  'settings.themeSystem': m('System', '跟隨系統'),
  'settings.themeLight': m('Light', '淺色'),
  'settings.themeDark': m('Dark', '深色'),
  'settings.fontSize': m('Text size', '字體大小'),
  'settings.fontCozy': m('Compact', '緊湊'),
  'settings.fontNormal': m('Standard', '標準'),
  'settings.fontLarge': m('Large', '大字'),
  'settings.practice': m('Practice', '練習行為'),
  'settings.reveal': m('Show answers immediately in practice', '練習模式即時顯示答案'),
  'settings.revealHint': m('Turn off to reveal only at the end.', '關閉後，練習也要結束才看結果。'),
  'settings.autoNext': m('Advance after a correct answer', '答對後自動跳下一題'),
  'settings.autoNextHint': m('Practice and review only.', '只影響練習與複習模式。'),
  'settings.timer': m('Show the test countdown', '顯示測驗倒數'),
  'settings.shuffle': m('Shuffle questions', '打亂題目次序'),
  'settings.shuffleHint': m('Applied when a session starts.', '每次開始練習時隨機排序。'),
  'settings.goals': m('Goals and timing', '目標與時間'),
  'settings.dailyTarget': m('Daily target', '每日目標題數'),
  'settings.dailyTargetHint': m('Drives the figure on Today.', '顯示在今日頁的進度。'),
  'settings.examLength': m('Exam length', '考試時間'),
  'settings.examLengthHint': m(
    'Set by each deck: official decks publish their own length and AI-generated decks set one while generating. Other decks are estimated from their questions.',
    '由每份題庫決定：官方題庫會公佈自己的時限，AI 出題時亦會設定；其他題庫按題目數量與難度估算。',
  ),
  'settings.autoNextMs': m('Auto-advance delay', '自動跳題延遲'),
  'settings.ai': m('AI question generation', 'AI 出題'),
  'settings.aiHint': m(
    'Use your own OpenAI-compatible endpoint. The key stays on this device and is never included in backups.',
    '使用你自己的 OpenAI 相容 API。金鑰只存在這部裝置，不會寫入備份。',
  ),
  'settings.aiSignIn': m('Sign in to save an API key.', '請先登入才能儲存 API 金鑰。'),
  'settings.aiSignInBody': m(
    'API keys are account settings. Sign in to add your own provider and generate questions; guests can keep practising the starter decks.',
    'API 金鑰屬於帳戶設定；登入後才能加入自己的供應商並出題。訪客可繼續練習入門題庫。',
  ),
  'settings.guestProgressHint': m(
    'Guest practice is kept on this device and carried into a new account. Existing accounts keep their own statistics.',
    '訪客練習會留在這部裝置，並帶入新帳戶；已有帳戶會保留自己的統計。',
  ),
  'settings.aiBaseUrl': m('API base URL', 'API 網址'),
  'settings.aiBaseUrlHint': m(
    'Include the version path, e.g. https://api.openai.com/v1',
    '要包含版本路徑，例如 https://api.openai.com/v1',
  ),
  'settings.aiKey': m('API key', 'API 金鑰'),
  'settings.aiKeyHint': m(
    'Stored locally. Leave empty for a local model server.',
    '只存本機。使用本機模型伺服器時可留空。',
  ),
  'settings.aiModel': m('Model', '模型'),
  'settings.aiTemperature': m('Temperature', '隨機度'),
  'settings.aiMaxTokens': m('Max reply tokens', '回覆 Token 上限'),
  'settings.aiMaxTokensHint': m(
    'Output budget for one batch. The app automatically splits a batch if the model hits this limit.',
    '每批回覆的輸出上限。若模型達到此上限，程式會自動把批次拆小。',
  ),
  'settings.aiJsonMode': m('Ask for JSON mode', '使用 JSON 模式'),
  'settings.aiJsonModeHint': m(
    'Turn off if your provider rejects response_format.',
    '如果供應商不支援 response_format，請關閉。',
  ),
  'settings.aiTest': m('Test connection', '測試連線'),
  'settings.aiTesting': m('Testing…', '測試中…'),
  'settings.aiOk': m('Connected to {model}', '已連線：{model}'),
  'settings.aiFail': m('Connection failed: {message}', '連線失敗：{message}'),
  'settings.aiNotConfigured': m('Add a base URL and model first.', '請先填寫 API 網址與模型。'),
  'settings.aiSave': m('Save AI settings', '儲存 AI 設定'),
  'settings.aiSaved': m('AI settings saved', 'AI 設定已儲存'),
  'settings.aiCodeNote': m(
    'Code questions run only in practice sessions. Exams accept the answer but never execute it.',
    '程式題只在練習時執行；考試模式只接受答案，不會執行。',
  ),
  'settings.data': m('Data', '資料'),
  'settings.storage': m('local cache ({kind})', '本機快取（{kind}）'),
  'settings.export': m('Export backup', '匯出備份'),
  'settings.import': m('Import backup', '匯入備份'),
  'settings.reset': m('Reset progress', '重設進度'),
  'settings.dataHint': m(
    'Backups include attempts, schedule, flags, notes and imported packs. The cloud copy updates automatically.',
    '備份包含作答紀錄、排程、標記、筆記與本機插件；雲端副本會自動更新。',
  ),
  'settings.exported': m('Backup exported', '已匯出備份'),
  'settings.imported': m('Backup restored', '已還原備份'),
  'settings.resetTitle': m('Reset all progress?', '重設所有學習進度？'),
  'settings.resetBody': m('Attempts, schedule, mistakes and notes will be cleared. Settings are kept. This cannot be undone.', '作答紀錄、排程、錯題與筆記都會清除，設定會保留，無法復原。'),
  'settings.resetConfirm': m('Reset', '確定重設'),
  'settings.resetDone': m('Progress reset', '學習進度已重設'),
  'settings.syncHint': m(
    'Progress syncs automatically across your signed-in devices. Theme and language stay on this device.',
    '學習進度會在已登入的裝置之間自動同步；主題與語言則留在這部裝置。',
  ),
  'settings.syncSignedOut': m('Sign in to sync progress across devices.', '登入後可在裝置之間同步學習進度。'),
  'settings.syncNow': m('Sync now', '立即同步'),
  'settings.syncSyncing': m('Syncing…', '同步中…'),
  'settings.syncIdle': m('Up to date', '已同步'),
  'settings.syncFailed': m('Sync failed — retrying', '同步失敗，會自動重試'),
  'settings.syncWaiting': m('Waiting for cloud', '等待雲端'),
  'settings.shortcuts': m('Keyboard', '快捷鍵'),
  'settings.shortcutChoose': m('Choose an option', '選擇選項'),
  'settings.shortcutEnter': m('Submit or next', '提交／下一題'),
  'settings.shortcutMove': m('Previous / next', '上一題／下一題'),
  'settings.shortcutFlag': m('Flag question', '標記題目'),
  'settings.about': m('About', '關於'),
  'settings.aboutApp': m('App', '應用'),
  'settings.aboutQuestions': m('Questions', '題目'),
  'settings.aboutAttempts': m('Answers recorded', '作答紀錄'),
  'settings.aboutStorage': m('Storage', '儲存'),
  'settings.storageIndexedDb': m('This device (IndexedDB)', '本機 IndexedDB'),
  'settings.storageMemory': m('Memory only — reload will lose it', '只存記憶體，重新載入會遺失'),
  'settings.questionsValue': m('{n} questions · {m} plugins', '{n} 題 · {m} 個插件'),

  'official.badge': m('Official', '官方'),
  'official.loading': m('Loading the official catalogue…', '載入官方題庫中…'),
  'official.syncing': m('Checking for updates…', '檢查更新中…'),
  'official.network': m('Official catalogue v{version}', '官方題庫 v{version}'),
  'official.cache': m('Official catalogue v{version} (cached)', '官方題庫 v{version}（快取）'),
  'official.bundled': m('Using the bundled question bank', '使用內建題庫'),
  'official.disabled': m('Backend not configured — using bundled content', '未設定後端，使用內建題庫'),
  'official.failed': m('Could not reach the official catalogue', '無法連接官方題庫'),
  'official.stale': m('Offline — using the last synced catalogue', '離線，使用上次同步的題庫'),
  'official.lastSync': m('Last synced {date}', '上次同步：{date}'),
  'official.neverSynced': m('Not synced on this device yet', '這部裝置尚未同步'),
  'official.issues': m('{n} content issue(s) were skipped', '略過了 {n} 個內容問題'),
  'official.check': m('Check for updates', '檢查更新'),
  'official.checked': m('Official catalogue is up to date', '官方題庫已是最新'),

  'plugins.official': m('Official', '官方'),
  'plugins.officialHint': m(
    'Official decks are published in Supabase and shown in the Plaza. Add one there to put it in your own library; plugins only add functionality.',
    '官方題庫發布在 Supabase 並顯示於廣場；在廣場加入後才會進入你的題庫。插件只負責功能。',
  ),

  'account.title': m('Account', '帳戶'),
  'account.lede': m('Your sign-in, profile and official catalogue.', '登入、個人資料與官方題庫。'),
  'account.signIn': m('Sign in', '登入'),
  'account.signedInHint': m('Profile & backend', '個人資料與後端'),
  'account.signInHint': m('Save your profile', '儲存個人資料'),
  'account.saved': m('Profile saved', '個人資料已儲存'),
  'account.signedOut': m('Signed out', '已登出'),
  'account.signOut': m('Sign out', '登出'),
  'account.recoveryNotice': m('You are resetting your password.', '你正在重設密碼。'),
  'account.resetPassword': m('Reset password', '重設密碼'),
  'account.unnamed': m('Unnamed learner', '未命名學習者'),
  'account.verified': m('Verified', '已驗證'),
  'account.displayName': m('Display name', '顯示名稱'),
  'account.displayNameHint': m('Shown in your profile and on this device.', '顯示於個人資料與這部裝置。'),
  'account.save': m('Save profile', '儲存個人資料'),
  'account.role': m('Role', '角色'),
  'account.provider': m('Sign-in method', '登入方式'),
  'account.memberSince': m('Member since', '加入日期'),
  'account.cloudDecks': m('Cloud decks', '雲端題庫'),
  'account.officialDecks': m('Official decks in my library', '已加入的官方題庫'),
  'account.backend': m('Backend & official content', '後端與官方內容'),
  'account.connected': m('Configured', '已設定'),
  'account.notConnected': m('Not configured', '未設定'),
  'account.backendHint': m(
    'Official packs are read from Supabase and cached on this device. A new catalogue version is pulled automatically; the button checks immediately.',
    '官方題庫從 Supabase 讀取並快取在這部裝置。目錄版本更新時會自動下載，按鈕可立即檢查。',
  ),
  'account.localNote': m(
    'Decks you generate or import upload automatically to your private account; only you can read them.',
    '你生成或匯入的題庫會自動上傳到你的私人帳戶，只有你能讀取。',
  ),
  'account.securityNote': m(
    'Official content is read-only for learners. Row-level security on the server rejects writes from ordinary accounts.',
    '學習者對官方內容只有讀取權限；伺服器的資料列級安全政策會拒絕普通帳戶的寫入。',
  ),
  'account.admin': m('Administrator', '管理員'),
  'account.user': m('Learner', '學習者'),

  'auth.eyebrow': m('MengYin Study', 'MengYin Study'),
  'auth.asideTitle': m('Your revision, kept where it belongs.', '你的溫習，放在它該在的地方。'),
  'auth.asideBody': m(
    'The Plaza carries official question banks. Add the ones you want, and your own generated decks follow your account automatically.',
    '廣場提供官方題庫；把想練的加入自己的題庫，而你自己生成的題庫會自動跟隨帳戶。',
  ),
  'auth.asidePoint1': m(
    'Official decks update automatically when the catalogue changes.',
    '官方題庫目錄一更新，App 內的題庫就會同步。',
  ),
  'auth.asidePoint2': m(
    'Generated and imported decks upload automatically to your private account.',
    'AI 生成與匯入的題庫會自動上傳到你的私人帳戶。',
  ),
  'auth.asidePoint3': m(
    'Google, GitHub or email — one account for your profile.',
    'Google、GitHub 或電郵——一個帳戶管理你的個人資料。',
  ),
  'auth.signin.eyebrow': m('Welcome back', '歡迎回來'),
  'auth.signin.title': m('Sign in', '登入'),
  'auth.signin.lede': m('Continue your streak, or use the official catalogue.', '繼續你的學習紀錄，或使用官方題庫。'),
  'auth.signup.eyebrow': m('New account', '新帳戶'),
  'auth.signup.title': m('Create an account', '建立帳戶'),
  'auth.signup.lede': m('Email and password, or a provider you already use.', '用電郵與密碼，或你已有的第三方帳戶。'),
  'auth.forgot.eyebrow': m('Password help', '密碼協助'),
  'auth.forgot.title': m('Reset your password', '重設密碼'),
  'auth.forgot.lede': m('We will email a recovery link if the address has an account.', '如果該電郵有帳戶，我們會寄出重設連結。'),
  'auth.reset.eyebrow': m('New password', '新密碼'),
  'auth.reset.title': m('Choose a new password', '選擇新密碼'),
  'auth.reset.lede': m('Use at least 8 characters. You will stay signed in on this device.', '至少 8 個字元；完成後會保持登入。'),
  'auth.google': m('Continue with Google', '使用 Google 繼續'),
  'auth.github': m('Continue with GitHub', '使用 GitHub 繼續'),
  'auth.or': m('or use email', '或使用電郵'),
  'auth.displayName': m('Display name', '顯示名稱'),
  'auth.email': m('Email', '電郵'),
  'auth.password': m('Password', '密碼'),
  'auth.confirmPassword': m('Confirm password', '確認密碼'),
  'auth.show': m('Show', '顯示'),
  'auth.hide': m('Hide', '隱藏'),
  'auth.showPassword': m('Show password', '顯示密碼'),
  'auth.hidePassword': m('Hide password', '隱藏密碼'),
  'auth.forgot': m('Forgot password?', '忘記密碼？'),
  'auth.signIn': m('Sign in', '登入'),
  'auth.createAccount': m('Create account', '建立帳戶'),
  'auth.sendReset': m('Send reset link', '寄出重設連結'),
  'auth.backToSignIn': m('Back to sign in', '返回登入'),
  'auth.noAccount': m('No account yet?', '還沒有帳戶？'),
  'auth.haveAccount': m('Already have an account?', '已有帳戶？'),
  'auth.guest': m('Continue as guest', '以訪客身分繼續'),
  'auth.checkEmailEyebrow': m('One more step', '還差一步'),
  'auth.checkEmailTitle': m('Check your email', '請檢查電郵'),
  'auth.checkEmailLede': m(
    'Confirm your address to finish creating the account.',
    '確認電郵地址即可完成註冊。',
  ),
  'auth.checkEmailBody': m(
    'We sent a confirmation link to {email}. Open it on this device to finish signing in.',
    '我們已把確認連結寄到 {email}。請在這部裝置開啟連結完成登入。',
  ),
  'auth.resend': m('Resend email', '重新寄出'),
  'auth.resendDone': m('Email sent', '已寄出'),
  'auth.resetSentEyebrow': m('Check your inbox', '請檢查信箱'),
  'auth.resetSentTitle': m('Recovery link sent', '已寄出重設連結'),
  'auth.resetSentLede': m('The link expires after a short while.', '連結會在一段時間後失效。'),
  'auth.resetSentBody': m('If {email} has an account, a reset link is on its way.', '如果 {email} 有帳戶，重設連結正在寄出。'),
  'auth.localNote': m(
    'Guest practice stays on this device and is carried into a new account. Signing in unlocks the Plaza, AI generation and private cloud sync.',
    '訪客練習會留在這部裝置，並帶入新帳戶。登入後可使用廣場、AI 出題與私人雲端同步。',
  ),
  'auth.account': m('Account', '帳戶'),
  'auth.resetFor': m('Resetting the password for {email}.', '正在重設 {email} 的密碼。'),
  'auth.newPassword': m('New password', '新密碼'),
  'auth.updatePassword': m('Update password', '更新密碼'),
  'auth.working': m('Working…', '處理中…'),
  'auth.validation.email': m('Enter a valid email address.', '請輸入有效的電郵地址。'),
  'auth.validation.displayName': m('Enter a display name.', '請輸入顯示名稱。'),
  'auth.validation.password': m('Use at least 8 characters for the password.', '密碼至少要有 8 個字元。'),
  'auth.validation.match': m('The two passwords do not match.', '兩次輸入的密碼不一致。'),
  'auth.resetLinkInvalidTitle': m('This link is no longer valid', '這個連結已失效'),
  'auth.resetLinkInvalidBody': m(
    'Request a new password-reset email, or sign in if you remember your password.',
    '請重新申請重設電郵；如果記得密碼，也可以直接登入。',
  ),
  'auth.unconfiguredBody': m(
    'Accounts are unavailable because Supabase is not configured. Add VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY to .env, then rebuild.',
    '尚未設定 Supabase，帳戶功能暫不可用。請在 .env 加入 VITE_SUPABASE_URL 與 VITE_SUPABASE_PUBLISHABLE_KEY，然後重新建置。',
  ),
  'auth.error.unconfigured': m('The account service is not configured.', '帳戶服務尚未設定。'),
  'auth.error.notReady': m(
    'The backend is reachable but its tables are missing. Apply the Supabase migrations.',
    '後端可連接，但資料表尚未建立。請先套用 Supabase migration。',
  ),
  'auth.error.network': m('Network error. Check your connection and try again.', '網絡錯誤，請檢查連線後重試。'),
  'auth.error.server': m('The server had a problem. Try again in a moment.', '伺服器發生問題，請稍後再試。'),
  'auth.error.unauthorized': m('Your session has expired. Sign in again.', '登入已逾時，請重新登入。'),
  'auth.error.forbidden': m('You do not have permission to do that.', '你沒有權限進行這個操作。'),
  'auth.error.invalidCredentials': m('Email or password is incorrect.', '電郵或密碼不正確。'),
  'auth.error.emailUnconfirmed': m('Confirm your email address before signing in.', '請先確認電郵地址再登入。'),
  'auth.error.emailTaken': m('An account with that email already exists.', '這個電郵已有帳戶。'),
  'auth.error.weakPassword': m('Use at least 8 characters for the password.', '密碼至少要有 8 個字元。'),
  'auth.error.samePassword': m('The new password must be different.', '新密碼必須與舊密碼不同。'),
  'auth.error.rateLimited': m('Too many attempts. Wait a moment and try again.', '嘗試次數過多，請稍後再試。'),
  'auth.error.linkExpired': m('That link has expired. Request a new one.', '連結已失效，請重新申請。'),
  'auth.error.providerDisabled': m(
    'That sign-in provider is not enabled for this project.',
    '這個登入供應商尚未在此專案啟用。',
  ),
  'auth.error.signupDisabled': m('New sign-ups are disabled for this project.', '此專案已停用新註冊。'),
  'auth.error.profileMissing': m('Your profile could not be loaded. Try refreshing.', '無法載入個人資料，請重新整理。'),
  'auth.error.invalidData': m('Check the form and try again.', '請檢查表格內容後重試。'),

  'toast.imported': m('Imported "{name}"', '已匯入「{name}」'),
  'toast.removed': m('Plugin removed', '已移除插件'),
  'toast.importFailed': m('Import failed: {message}', '匯入失敗：{message}'),
  'toast.nothing': m('Nothing to practise in this set', '這組題目暫時沒有可練習的內容'),
  'toast.answerFirst': m('Finish your answer first', '請先完成作答'),
  'toast.reloaded': m('Reloaded {n} plugins', '已重新載入 {n} 個插件'),
  'toast.backupInvalid': m('Invalid backup: {message}', '備份檔無效：{message}'),
  'toast.unrecognised': m('Could not read that plugin', '無法辨識插件內容'),
  'toast.codeRunFailed': m('Code could not run: {message}', '程式碼無法執行：{message}'),
  'toast.materialsAdded': m('Added {n} material(s)', '已加入 {n} 份材料'),
  'toast.materialsCleared': m(
    'Materials deleted after generation',
    '生成完成，材料已刪除',
  ),
  'toast.materialRemoved': m('Material removed', '已移除材料'),
  'toast.deckDeleted': m('Removed “{name}” from your library', '已從題庫移除「{name}」'),
  'toast.deckNotFound': m('That deck is no longer in your library.', '這個題庫已不在你的題庫中。'),
  'toast.guestDeckLocked': m('The starter deck is always available.', '入門題庫永遠可用。'),
  'toast.deckGenerated': m('Saved "{name}"', '已儲存「{name}」'),
  'toast.saveNeedsLogin': m(
    'Sign in to save generated decks',
    '請先登入才能儲存生成的題庫',
  ),
  'toast.deckSaveUploadFailed': m(
    'Saved locally, but the cloud upload failed: {message}',
    '已存在本機，但上傳到雲端失敗：{message}',
  ),
} as const satisfies Record<string, { en: string; zh: string }>;

export type MessageKey = keyof typeof messages;

export type Translate = (key: MessageKey, vars?: Record<string, string | number>) => string;

export function createTranslator(locale: Locale): Translate {
  return (key, vars) => {
    const entry = messages[key];
    const template = locale === 'zh-Hant' ? entry.zh : entry.en;
    if (!vars) return template;
    return template.replace(/\{(\w+)\}/g, (_, name: string) => {
      const value = vars[name];
      return value === undefined ? `{${name}}` : String(value);
    });
  };
}
